import { Component, computed, effect, inject, input, OnDestroy, output, ChangeDetectionStrategy } from "@angular/core";
import { FormArray, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import _ from "lodash";
import { QueryNodeRowComponent } from "rl-common/components/advanced-search/advanced-search-builder/query-node-row/query-node-row.component";
import { AdvancedSearch } from "rl-common/components/advanced-search/rl-advanced-search.consts";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { IQueryNode } from "rl-common/models/i-query-node";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { Subscription } from "rxjs";

interface IOperatorOption {
	key: string;
	value: string;
}

@Component({
	selector: "rl-task-condition-builder",
	imports: [FormsModule, ReactiveFormsModule, DropdownSingleComponent, QueryNodeRowComponent],
	templateUrl: "./task-condition-builder.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: "./task-condition-builder.component.scss",
})
export class TaskConditionBuilderComponent implements OnDestroy {

	charTypeId = input.required<number>();
	queryNode = input<IQueryNode | null>(null);

	queryNodeChange = output<IQueryNode | null>();

	private readonly _oneConfig = inject(OneConfigService);
	private _subs: Subscription[] = [];
	private _lastEmittedNode: IQueryNode | null = null;

	rows = new FormArray<FormControl<IQueryNode>>([]);

	operatorOptions: DropdownOptions<IOperatorOption> = {
		items: [
			{ key: "$and", value: "ALL" },
			{ key: "$or", value: "ANY" }
		],
		rowKeyFn: (o) => o.key,
		rowLabelFn: (o) => o.value
	};
	selectedOperator: IOperatorOption = this.operatorOptions.items[0];

	strategies = computed<AdvancedSearch.IQueryNodeJoinStrategy[]>(() => {
		const charTypeId = this.charTypeId();
		const parentCharTypes = new Set(this._oneConfig.getParentAssocCharTypeIds(charTypeId));
		const childCharTypes = new Set(this._oneConfig.getChildAssocCharTypeIds(charTypeId));
		const divParties = this._oneConfig.getDivParties();

		const templateParties = _(this._oneConfig.getTemplates(charTypeId))
			.flatMap((template) => this._oneConfig.getParties(charTypeId, template.templateID))
			.uniqBy(party => party.partyID)
			.value();

		const parties = divParties.filter(d =>
			templateParties.some(t => t.partyID === d.partyID)
		);

		return AdvancedSearch.getQueryNodeStrategies(charTypeId, parentCharTypes, childCharTypes, parties);
	});

	constructor() {
		effect(() => {
			const queryNode = this.queryNode();
			if (!_.isEqual(queryNode, this._lastEmittedNode)) {
				this.initializeRows(queryNode);
			}
		});
	}

	private initializeRows(queryNode: IQueryNode | null) {
		this._subs.forEach(s => s.unsubscribe());
		this._subs = [];

		this.rows.clear();

		if (queryNode) {
			this.hydrateFromQueryNode(queryNode);
		}

		this.rows.controls.forEach(control => {
			this.subscribeToControl(control);
		});
	}

	private subscribeToControl(control: FormControl<IQueryNode>) {
		const sub = control.valueChanges.subscribe(() => {
			this.emitQueryNode();
		});
		this._subs.push(sub);
	}

	private hydrateFromQueryNode(queryNode: IQueryNode) {
		if (queryNode.$and) {
			this.selectedOperator = this.operatorOptions.items[0];
			queryNode.$and.forEach(child => {
				this.rows.push(new FormControl<IQueryNode>(child));
			});
		} else if (queryNode.$or) {
			this.selectedOperator = this.operatorOptions.items[1];
			queryNode.$or.forEach(child => {
				this.rows.push(new FormControl<IQueryNode>(child));
			});
		} else {
			this.rows.push(new FormControl<IQueryNode>(queryNode));
		}
	}

	addRow() {
		const control = new FormControl<IQueryNode>({});
		this.rows.push(control);
		this.subscribeToControl(control);
		this.emitQueryNode();
	}

	removeRow(index: number) {
		this.rows.removeAt(index);
		this.emitQueryNode();
	}

	onOperatorChange() {
		this.emitQueryNode();
	}

	private emitQueryNode() {
		const nodes = this.rows.controls
			.map(c => c.value)
			.filter(v => v != null && Object.keys(v).length > 0);

		let toEmit: IQueryNode | null;

		if (nodes.length === 0) {
			toEmit = null;
		} else {
			toEmit = this.selectedOperator.key === "$or"
				? { $or: nodes }
				: { $and: nodes };
		}

		this._lastEmittedNode = toEmit;
		this.queryNodeChange.emit(toEmit);
	}

	ngOnDestroy() {
		this._subs.forEach(s => s.unsubscribe());
	}
}
